import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Flex, Text, Button, VStack, HStack, IconButton, Select, Badge,
  NumberInput, NumberInputField, Spinner, Progress, FormControl, FormLabel,
  Tabs, TabList, Tab, TabPanels, TabPanel, useDisclosure,
} from '@chakra-ui/react';
import { ArrowBackIcon, InfoIcon, DeleteIcon } from '@chakra-ui/icons';
import { useMICUTheme } from '../lib/micu/MICUThemeProvider';
import { db, updateMedication, deleteMedication, getPatient } from '../lib/micu/micuDatabase';
import { getMedicationTemplate } from '../lib/micu/medicationTemplates';
import { calculateDoseFromRate, calculateRateFromDose, calculateTimeRemaining, formatMinutesAsHHMM } from '../lib/micu/calculations';
import MedicationDoseInfo from '../components/micu/MedicationDoseInfo';

const MICUMedDetail = () => {
  const { id, medId } = useParams();
  const caseId = Number(id);
  const medicationId = Number(medId);
  const navigate = useNavigate();
  const { isDark } = useMICUTheme();

  const [med, setMed] = useState(null);
  const [template, setTemplate] = useState(null);
  const [patient, setPatient] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driver, setDriver] = useState('rate'); // 'rate' or 'dose'
  const doseInfoModal = useDisclosure();

  const loadData = useCallback(async () => {
    setLoading(true);
    const m = await db.medications.get(medicationId);
    setMed(m);
    if (m?.templateId) setTemplate(getMedicationTemplate(m.templateId));
    const p = await getPatient(caseId);
    setPatient(p);
    const allLogs = await db.medicationLogs.where('medicationId').equals(medicationId).toArray();
    setLogs(allLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
    setLoading(false);
  }, [medicationId, caseId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleFieldChange = async (field, value) => {
    if (!med) return;
    const oldValue = med[field];
    const numValue = value === '' ? null : Number(value);

    // Update local state
    const updated = { ...med, [field]: numValue };

    // Auto-calculate based on driver
    const patientWeight = patient?.weightKg || null;

    if (field === 'rate' && driver === 'rate' && numValue != null) {
      const calculatedDose = calculateDoseFromRate(
        numValue, updated.concentration, updated.concentrationUnit, updated.doseUnit, patientWeight
      );
      if (calculatedDose != null) {
        updated.dose = calculatedDose;
        await updateMedication(medicationId, 'dose', oldValue, calculatedDose);
      }
    } else if (field === 'dose' && driver === 'dose' && numValue != null) {
      const calculatedRate = calculateRateFromDose(
        numValue, updated.concentration, updated.concentrationUnit, updated.doseUnit, patientWeight
      );
      if (calculatedRate != null) {
        updated.rate = calculatedRate;
        await updateMedication(medicationId, 'rate', med.rate, calculatedRate);
      }
    }

    setMed(updated);
    await updateMedication(medicationId, field, oldValue, numValue);
    // Refresh logs
    const allLogs = await db.medicationLogs.where('medicationId').equals(medicationId).toArray();
    setLogs(allLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
  };

  const handleConcentrationChange = async (concValue) => {
    if (!med || !template) return;
    const conc = template.defaultConcentrations.find(c => c.value === Number(concValue));
    if (!conc) return;
    await updateMedication(medicationId, 'concentration', med.concentration, conc.value);
    await updateMedication(medicationId, 'concentrationUnit', med.concentrationUnit, conc.unit);
    setMed({ ...med, concentration: conc.value, concentrationUnit: conc.unit });
  };

  const handleDelete = async () => {
    await deleteMedication(medicationId);
    navigate(`/micu-mobile/case/${caseId}`);
  };

  if (loading) {
    return <Flex justify="center" align="center" minH="300px"><Spinner color="micu.teal" size="lg" /></Flex>;
  }

  if (!med) {
    return <Box p={4}><Text color="micu.red">Medication not found.</Text></Box>;
  }

  const timeRemaining = calculateTimeRemaining(med.vtbi, med.rate);
  const timeRemainingStr = formatMinutesAsHHMM(timeRemaining);
  const vtbiProgress = med.vtbi && med.rate && med.startedAt
    ? Math.max(0, Math.min(100, ((med.vtbi - (med.rate * ((Date.now() - new Date(med.startedAt).getTime()) / 3600000))) / med.vtbi) * 100))
    : null;

  return (
    <Box p={4}>
      <Flex justify="space-between" align="center" mb={4}>
        <HStack spacing={2}>
          <IconButton
            icon={<ArrowBackIcon />}
            variant="micuGhost"
            size="sm"
            aria-label="Back"
            onClick={() => navigate(`/micu-mobile/case/${caseId}`)}
          />
          <Text fontSize="lg" fontWeight="bold" color="micu.text">{med.drugName}</Text>
        </HStack>
        <HStack spacing={2}>
          <IconButton
            icon={<InfoIcon />}
            variant="micuGhost"
            size="sm"
            aria-label="Dose info"
            onClick={doseInfoModal.onOpen}
          />
          <IconButton
            icon={<DeleteIcon />}
            variant="micuDanger"
            size="sm"
            aria-label="Delete medication"
            onClick={handleDelete}
          />
        </HStack>
      </Flex>

      <Tabs variant="line" colorScheme="teal" size="sm">
        <TabList borderColor="micu.border">
          <Tab color="micu.textSecondary" _selected={{ color: 'micu.teal', borderColor: 'micu.teal' }}>Infusion</Tab>
          <Tab color="micu.textSecondary" _selected={{ color: 'micu.teal', borderColor: 'micu.teal' }}>Log</Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0}>
            <VStack spacing={4} align="stretch">
              {/* Concentration */}
              <FormControl>
                <FormLabel fontSize="sm" color="micu.textSecondary">Concentration</FormLabel>
                {template ? (
                  <Select
                    variant="micu"
                    size="sm"
                    value={med.concentration}
                    onChange={(e) => handleConcentrationChange(e.target.value)}
                  >
                    {template.defaultConcentrations.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </Select>
                ) : (
                  <Text fontSize="sm" color="micu.text">{med.concentration} {med.concentrationUnit}</Text>
                )}
              </FormControl>

              {/* Driver toggle */}
              <HStack spacing={2}>
                <Text fontSize="xs" color="micu.textMuted">Calculate:</Text>
                <Button
                  size="xs"
                  variant={driver === 'dose' ? 'micuPrimary' : 'micuGhost'}
                  onClick={() => setDriver('dose')}
                >
                  Rate from Dose
                </Button>
                <Button
                  size="xs"
                  variant={driver === 'rate' ? 'micuPrimary' : 'micuGhost'}
                  onClick={() => setDriver('rate')}
                >
                  Dose from Rate
                </Button>
              </HStack>

              {/* Dose */}
              <FormControl>
                <FormLabel fontSize="sm" color="micu.textSecondary">
                  Dose ({med.doseUnit})
                  {driver === 'dose' && <Badge ml={2} colorScheme="teal" fontSize="2xs">Driver</Badge>}
                </FormLabel>
                <NumberInput
                  size="sm"
                  value={med.dose ?? ''}
                  onChange={(v) => handleFieldChange('dose', v)}
                  min={0}
                >
                  <NumberInputField
                    bg={isDark ? 'micu.bgSecondary' : 'micu.bg'}
                    border="1px solid"
                    borderColor={driver === 'dose' ? 'micu.teal' : 'micu.border'}
                    color="micu.text"
                  />
                </NumberInput>
              </FormControl>

              {/* Rate */}
              <FormControl>
                <FormLabel fontSize="sm" color="micu.textSecondary">
                  Rate ({med.rateUnit})
                  {driver === 'rate' && <Badge ml={2} colorScheme="teal" fontSize="2xs">Driver</Badge>}
                </FormLabel>
                <NumberInput
                  size="sm"
                  value={med.rate ?? ''}
                  onChange={(v) => handleFieldChange('rate', v)}
                  min={0}
                >
                  <NumberInputField
                    bg={isDark ? 'micu.bgSecondary' : 'micu.bg'}
                    border="1px solid"
                    borderColor={driver === 'rate' ? 'micu.teal' : 'micu.border'}
                    color="micu.text"
                  />
                </NumberInput>
              </FormControl>

              {/* VTBI */}
              <FormControl>
                <FormLabel fontSize="sm" color="micu.textSecondary">VTBI (mL)</FormLabel>
                <NumberInput
                  size="sm"
                  value={med.vtbi ?? ''}
                  onChange={(v) => handleFieldChange('vtbi', v)}
                  min={0}
                >
                  <NumberInputField
                    bg={isDark ? 'micu.bgSecondary' : 'micu.bg'}
                    border="1px solid"
                    borderColor="micu.border"
                    color="micu.text"
                  />
                </NumberInput>
              </FormControl>

              {/* Time remaining */}
              {timeRemaining != null && (
                <Box p={3} bg={isDark ? 'micu.bgTertiary' : 'micu.bgSecondary'} borderRadius="md" border="1px solid" borderColor="micu.border">
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" color="micu.textSecondary">Time Remaining</Text>
                    <Text fontSize="lg" fontWeight="bold" color={timeRemaining <= (med.alarmMinutes || 15) ? 'micu.red' : 'micu.teal'}>
                      {timeRemainingStr}
                    </Text>
                  </HStack>
                  {vtbiProgress != null && (
                    <Progress
                      value={vtbiProgress}
                      size="sm"
                      borderRadius="full"
                      colorScheme={vtbiProgress < 20 ? 'red' : vtbiProgress < 50 ? 'yellow' : 'green'}
                      bg={isDark ? 'micu.bgSecondary' : 'micu.bgTertiary'}
                    />
                  )}
                </Box>
              )}

              {/* Alarm setting */}
              <FormControl>
                <FormLabel fontSize="sm" color="micu.textSecondary">Alert when ___ minutes remaining</FormLabel>
                <NumberInput
                  size="sm"
                  value={med.alarmMinutes ?? 15}
                  onChange={(v) => handleFieldChange('alarmMinutes', v)}
                  min={1}
                  max={120}
                >
                  <NumberInputField
                    bg={isDark ? 'micu.bgSecondary' : 'micu.bg'}
                    border="1px solid"
                    borderColor="micu.border"
                    color="micu.text"
                  />
                </NumberInput>
              </FormControl>
            </VStack>
          </TabPanel>

          <TabPanel px={0}>
            <VStack spacing={2} align="stretch" maxH="400px" overflowY="auto">
              {logs.length === 0 ? (
                <Text fontSize="sm" color="micu.textMuted" textAlign="center" py={4}>No changes logged yet.</Text>
              ) : (
                logs.map(log => {
                  const time = new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
                  return (
                    <Box key={log.id} p={2} bg={isDark ? 'micu.bgTertiary' : 'micu.bgSecondary'} borderRadius="sm">
                      <HStack spacing={2}>
                        <Text fontSize="2xs" color="micu.textMuted">{time}</Text>
                        <Text fontSize="xs" color="micu.textSecondary">
                          {log.field}: {log.oldValue} → {log.newValue}
                        </Text>
                      </HStack>
                    </Box>
                  );
                })
              )}
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <MedicationDoseInfo
        isOpen={doseInfoModal.isOpen}
        onClose={doseInfoModal.onClose}
        caseId={caseId}
        templateId={med.templateId}
        currentDose={med.dose}
        doseUnit={med.doseUnit}
      />
    </Box>
  );
};

export default MICUMedDetail;
